import type { ModuleAccessLevelEnum } from "@/lib/lecturehub/types";

/**
 * Every "what stands between this viewer and this module" decision, in one
 * place, so CourseStartCard, EventStartCard, PaywallCard and MediaSidebar
 * cannot drift apart on it.
 *
 * THE MODEL — `accessLevel` is the whole answer; `Module.free` is OBSOLETE and
 * is deliberately not read here. On a single-payment portal:
 *
 *   PUBLIC      full access, no account needed
 *   REGISTERED  enrolment only (costs nothing, but you must be signed in)
 *   PREMIUM     payable OR enrolment — a price means buy, no price means enrol
 *
 * On a subscription portal PREMIUM means the subscription instead. So a module
 * with no price is never a dead end: the unlock is an enrolment, which the
 * caller's own enroll button already offers.
 *
 * Reading `free` as "no barrier" was wrong in both directions — it hid the
 * Subscribe CTA on a free PREMIUM module, and it made a priceless PREMIUM module
 * look permanently unavailable when it is simply enrol-to-watch.
 */
export type PaywallVariant =
  /** Not signed in. Every rung above PUBLIC needs an account first, and where a
   *  PUBLIC module is blocked it is a user-group restriction, which signing in
   *  is exactly what may clear. */
  | "login"
  /** Nothing to sell: the unlock is an enrolment (or the ladder is not what is
   *  blocking this viewer), so the caller's enroll button owns the CTA. */
  | "none"
  /** Single-payment portal, PREMIUM, price set. */
  | "buy"
  /** Subscription portal, PREMIUM. */
  | "subscribe";

export function paywallVariant(input: {
  isAuthed: boolean;
  accessLevel?: ModuleAccessLevelEnum | null;
  /** Minor units (cents), like every other `price` in the schema. */
  price?: number | null;
  isSubscription: boolean;
}): PaywallVariant {
  const { isAuthed, accessLevel, price, isSubscription } = input;

  if (!isAuthed) return "login";

  // Signed in already satisfies REGISTERED, and PUBLIC asks for nothing at all.
  // Whatever is still blocking this viewer is a user group or the module's
  // status — not something a purchase would unlock.
  // `accessLevel` is nullable in the schema, but the backend migrated existing
  // modules onto the ladder when it shipped the field, so an absent value means
  // "not told" rather than "no restriction" — and PREMIUM is the only rung that
  // can need a CTA, so isPaidGate treats the two alike.
  if (!isPaidGate({ accessLevel, price, isSubscription })) return "none";
  return isSubscription && !(price != null && Number(price) > 0) ? "subscribe" : "buy";
}

/**
 * Is MONEY the gate on this module — as opposed to an account or a free
 * enrolment? Viewer-INDEPENDENT on purpose: it replaces the old `!free` test in
 * the enroll cards, which asked the same question of the module alone, so guest
 * behaviour is unchanged (a guest still sees "Buy €X" and gets bounced to login
 * by the checkout route).
 *
 * PaywallCard uses `paywallVariant` instead, because it renders the auth gate
 * itself and so must know the viewer.
 */
export function isPaidGate(input: {
  accessLevel?: ModuleAccessLevelEnum | null;
  price?: number | null;
  isSubscription: boolean;
}): boolean {
  // Open, or unlocked by an account the viewer can simply create — never money.
  if (input.accessLevel === "PUBLIC" || input.accessLevel === "REGISTERED") return false;
  // PREMIUM: the subscription on a subscription portal, the price on a
  // single-payment one. No price there means enrolment, which costs nothing.
  if (input.isSubscription) return true;
  return input.price != null && Number(input.price) > 0;
}

/**
 * Whether the module costs nothing — for the "Free" badge on cards and the
 * price line on detail. Derived from `price`, NOT from the obsolete `free`
 * flag. An enrolment-gated module is still free: it costs nothing to unlock.
 */
export function isFreeToUnlock(price: number | null | undefined): boolean {
  return price == null || Number(price) <= 0;
}

/**
 * @deprecated Superseded by `paywallVariant`, which knows about `accessLevel`.
 * Its "unavailable" case does not exist under the access-level model: a
 * priceless PREMIUM module on a single-payment portal is enrol-to-watch, not a
 * dead end. Kept only until the last caller is migrated.
 */
export type PaidLockKind = "buy" | "subscribe" | "unavailable";

export function paidLockKind(
  price: number | null | undefined,
  isSubscription: boolean,
): PaidLockKind {
  if (price != null && Number(price) > 0) return "buy";
  if (isSubscription) return "subscribe";
  return "unavailable";
}
